import { useState } from "react";
import type { PlayerGoal } from "@/lib/types";
import { CoachModal } from "./CoachModal";
import { PlayerDevelopmentProfile } from "@/features/feedback/PlayerDevelopmentProfile";
import { GoalList } from "@/features/feedback/components/GoalList";
import { useCoachPlayerDevelopment } from "@/features/feedback/hooks/useDevelopmentProfile";
import { useCoachGoalMutations } from "../hooks/useCoachGoals";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useToast } from "@/components/ui/toast";
import { errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Plus } from "lucide-react";

/**
 * Coach-side Player Development modal (M7) — opened from a Coach >
 * Roster row's "Development profile" button.
 *
 * Hosts the SHARED `<PlayerDevelopmentProfile>` painter with
 * `viewer={false}` — so it renders the coach-only Themes section
 * + linked accounts that the viewer Development sub-tab omits.
 *
 * Goal list: the painter's `goalsSlot` gets a coach-mode
 * `<GoalList>`. Status changes happen inline (a `<select>` →
 * `updateGoal`, no nested modal). But "+ New goal" / "Edit" need
 * the `GoalComposerModal`, which itself wraps `CoachModal` — and
 * two stacked `CoachModal`s would both grab Escape. So those two
 * bubble UP to the shell: it temporarily replaces this workspace
 * with the composer, then restores the same player plan afterward.
 *
 * Delete shows a `<ConfirmDialog>` — no nested modal stacking.
 *
 * Coach recent-note/clip/playlist rows are read-only (no
 * `onOpen*`) — the coach surface isn't a playback context.
 */

export type CoachDevelopmentModalProps = {
  playerId: string;
  /** Display name for the modal title while the profile loads;
   * the painter re-renders the full header from the payload. */
  playerName: string;
  onClose: () => void;
  /** Roster control that should regain focus after this workspace closes. */
  returnFocusTo?: HTMLElement | null;
  /** Open the goal composer in CREATE mode for this player —
   * the host closes this modal first (one-modal-at-a-time). */
  onNewGoal: (playerId: string) => void;
  /** Open the goal composer in EDIT mode — same one-modal rule. */
  onEditGoal: (goal: PlayerGoal) => void;
};

export function CoachDevelopmentModal({
  playerId,
  playerName,
  onClose,
  returnFocusTo,
  onNewGoal,
  onEditGoal,
}: CoachDevelopmentModalProps) {
  const toast = useToast();
  const devQuery = useCoachPlayerDevelopment(playerId);
  const goalMutations = useCoachGoalMutations();
  const [confirmDeleteGoal, setConfirmDeleteGoal] = useState<PlayerGoal | null>(null);

  const handleStatusChange = async (goalId: number, status: string) => {
    // `status` is a closed enum from the card's <select>.
    try {
      await goalMutations.updateGoal(goalId, {
        status: status as PlayerGoal["status"],
      });
      toast.success("Goal updated");
    } catch (err) {
      toast.error("Couldn't update goal", { description: errorMessage(err) });
    }
  };

  const handleDelete = (goal: PlayerGoal) => {
    setConfirmDeleteGoal(goal);
  };

  const confirmDeleteGoalFn = async () => {
    if (!confirmDeleteGoal) return;
    const goal = confirmDeleteGoal;
    setConfirmDeleteGoal(null);
    try {
      await goalMutations.deleteGoal(goal.id);
      toast.success("Goal deleted");
    } catch (err) {
      toast.error("Couldn't delete goal", { description: errorMessage(err) });
    }
  };

  return (
    <>
      {confirmDeleteGoal ? (
        <ConfirmDialog
          title="Delete goal?"
          message={`Delete the goal "${confirmDeleteGoal.title || "Player goal"}"? This can't be undone.`}
          confirmLabel="Delete"
          danger
          onConfirm={confirmDeleteGoalFn}
          onClose={() => setConfirmDeleteGoal(null)}
          data-testid="coach-development-delete-confirm"
        />
      ) : null}
      <CoachModal
        title="Player plan"
        description={`Development priorities, follow-through, and recent evidence for ${
          devQuery.data?.player.display_name || playerName
        }.`}
        size="workspace"
        bodyClassName="bg-canvas"
        headerActions={
          <Button
            type="button"
            size="sm"
            onClick={() => onNewGoal(playerId)}
            data-testid="coach-development-new-goal"
          >
            <Plus aria-hidden="true" />
            <span className="hidden sm:inline">New goal</span>
            <span className="sm:hidden">Goal</span>
          </Button>
        }
        onClose={onClose}
        returnFocusTo={returnFocusTo}
        data-testid="coach-development-modal"
      >
      {devQuery.isLoading ? (
        <p
          className="text-ink-muted text-sm font-mono px-3 py-6"
          data-testid="coach-development-loading"
        >
          Loading player plan…
        </p>
      ) : devQuery.isError ? (
        <p
          className="text-destructive font-mono text-sm px-3 py-6"
          data-testid="coach-development-error"
        >
          Could not load the player plan.
        </p>
      ) : devQuery.data ? (
        <PlayerDevelopmentProfile
          profile={devQuery.data}
          viewer={false}
          presentation="coach-workspace"
          goalsSlot={
            <GoalList
              goals={devQuery.data.active_goals}
              viewer={false}
              onChangeStatus={handleStatusChange}
              onEdit={onEditGoal}
              onDelete={handleDelete}
            />
          }
        />
      ) : null}
      </CoachModal>
    </>
  );
}
